import env from "./env";

const getToken = () => localStorage.getItem("token");

const queryString = (params = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  ).toString();
  return query ? `?${query}` : "";
};

const authenticatedGet = async (path, params) => {
  const token = getToken();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  try {
    const response = await fetch(env.url + path + queryString(params), {
      signal: controller.signal,
      headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    let body;
    try {
      body = await response.json();
    } catch {
      body = { message: "Unexpected server response." };
    }
    return response.ok ? body : { ...body, success: false };
  } catch (error) {
    return { success: false, message: error.name === "AbortError" ? "The request timed out." : (error.message || "Network error.") };
  } finally {
    clearTimeout(timer);
  }
};

const Get = {
  ProviderStatus: () => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/provider/status", {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  ProviderNetworks: (params) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/provider/networks" + queryString(params), {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  ProviderCatalog: (params) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/provider/catalog" + queryString(params), {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  WalletMeta: () => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/wallets/meta", {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  Wallets: (params) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/wallets" + queryString(params), {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  Wallet: (walletId) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/wallets/" + walletId, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  CustodyTransactions: (params) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/custody/transactions" + queryString(params), {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  InternalTransfers: (params) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/wallets/transfers" + queryString(params), {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  MonthlyRewardCatalog: () => authenticatedGet("/monthly-reward/catalog"),
  VipStatus: () => authenticatedGet("/vip/status"),
  MonthlyRewardPlans: (params) => authenticatedGet("/monthly-reward/plans", params),
  MonthlyRewardDashboard: () => authenticatedGet("/monthly-reward/dashboard"),
  ReferralDashboard: () => authenticatedGet("/referral/dashboard"),
  MonthlyRewardReferrals: (params) => authenticatedGet("/monthly-reward/referrals", params),
  ScipProducts: () => authenticatedGet("/scip/products"),
  ScipPlans: (params) => authenticatedGet("/scip/plans", params),
  ScipDashboard: () => authenticatedGet("/scip/dashboard"),
  SuperStakeAssets: () => authenticatedGet("/super-stake/assets"),
  SuperStakeOffers: (params) => authenticatedGet("/super-stake/offers", params),
  SuperStakes: (params) => authenticatedGet("/super-stake/stakes", params),
  SuperStakeDashboard: () => authenticatedGet("/super-stake/dashboard"),

  BrokerOrders: (params) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/broker/orders" + queryString(params), {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  BrokerOrder: (orderId) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/broker/orders/" + orderId, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  OrderBook: (marketId) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/order-book/" + marketId, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  MarketTrades: (marketId) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/market-trades/" + marketId, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  MarketAssets: (params) => authenticatedGet("/market/assets", params),
  Orders: (params) => authenticatedGet("/orders", params),
  BinanceDepth: async (symbol) => {
    const marketSymbol = String(symbol ?? "").replace(/[^a-z0-9]/gi, "").toUpperCase();
    const response = await fetch(`${env.url}/market/binance/depth${queryString({ symbol: marketSymbol, limit: 20 })}`, { headers: { Accept: "application/json" } });
    const body = await response.json();
    if (!response.ok) throw new Error(body?.message || "Market depth is unavailable.");
    return body;
  },
  BinanceMarketTrades: async (symbol) => {
    const marketSymbol = String(symbol ?? "").replace(/[^a-z0-9]/gi, "").toUpperCase();
    const response = await fetch(`${env.url}/market/binance/trades${queryString({ symbol: marketSymbol, limit: 20 })}`, { headers: { Accept: "application/json" } });
    const body = await response.json();
    if (!response.ok) throw new Error(body?.message || "Recent market trades are unavailable.");
    return body;
  },
  BinanceKlines: async (symbol, interval, limit = 120) => {
    const marketSymbol = String(symbol ?? "").replace(/[^a-z0-9]/gi, "").toUpperCase();
    const response = await fetch(`${env.url}/market/binance/klines${queryString({ symbol: marketSymbol, interval, limit })}`, { headers: { Accept: "application/json" } });
    const body = await response.json();
    if (!response.ok) throw new Error("Candlestick data is unavailable for this market.");
    return body;
  },
  BinanceRules: async (symbol) => {
    const marketSymbol = String(symbol ?? "").replace(/[^a-z0-9]/gi, "").toUpperCase();
    const response = await fetch(`${env.url}/market/binance/rules${queryString({ symbol: marketSymbol })}`, { headers: { Accept: "application/json" } });
    const body = await response.json();
    if (!response.ok) throw new Error(body?.message || "Market rules are unavailable.");
    return body.data;
  },
  BinanceTickers: async () => {
    const response = await fetch(`${env.url}/market/binance/tickers`, { headers: { Accept: "application/json" } });
    const body = await response.json();
    if (!response.ok || !Array.isArray(body?.data)) throw new Error("Market tickers are unavailable.");
    return body.data;
  },

  MyTrades: (params) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/my-trades" + queryString(params), {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  Ads: (params) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/ads" + queryString(params), {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  Ad: (adId) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/ads/" + adId, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  P2pTrades: (params) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/p2p-trades" + queryString(params), {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  P2pTrade: (tradeId) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/p2p-trades/" + tradeId, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },
};

export default Get;